import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import { FiPackage, FiPlus, FiTrash2 } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import {
  dateStrInAppTz,
  formatDateTimeTrInAppTz,
} from "../../../utils/appTime";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const formatDateTime = formatDateTimeTrInAppTz;

const formatDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dateStrInAppTz(dt);
};

const InventoryAdditions = () => {
  useTitle("Rovion MES - Envanter Girişleri");
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/inventory/additions",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [additions, setAdditions] = useState([]);
  const [search, setSearch] = useState("");
  const [detailsModal, setDetailsModal] = useState({
    isOpen: false,
    addition: null,
  });

  const addToast = (message, type = "info") => {
    const newToast = { id: Date.now(), message, type };
    setToasts((prev) => [...prev, newToast]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== newToast.id)),
      3000,
    );
  };

  const fetchAdditions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/inventory/additions`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setAdditions(res.data?.additions || []);
    } catch (err) {
      addToast("Envanter girişleri yüklenemedi", "danger");
      setAdditions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdditions();
  }, []);

  const handleDeleteAddition = (addition) => {
    if (!addition?._id) return;
    const adder = addition?.adderUserId?.name || "-";
    const when = formatDateTime(addition.createdAt);
    confirm({
      title: "Envanter girişini sil?",
      message: `${adder} · ${when} kaydı silinecek ve stok geri alınacak. Devam edilsin mi?`,
      appearance: "danger",
      confirmButtonText: "Sil",
      cancelButtonText: "İptal",
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/inventory/additions/${addition._id}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          addToast("Envanter girişi silindi", "success");
          if (detailsModal.addition?._id === addition._id) {
            setDetailsModal({ isOpen: false, addition: null });
          }
          fetchAdditions();
        } catch (err) {
          addToast(
            err.response?.data?.message || "Envanter girişi silinemedi",
            "danger",
          );
        }
      },
    });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return additions;
    return additions.filter((a) => {
      const adder = a?.adderUserId?.name || "";
      const itemsText = (a?.items || [])
        .map((it) => {
          const inv = it?.inventoryItemId;
          const name = inv?.partId?.name || inv?.name || "";
          const code = inv?.partId?.code || inv?.code || "";
          return `${name} ${code}`.trim();
        })
        .join(" ");
      return (
        adder.toLowerCase().includes(q) || itemsText.toLowerCase().includes(q)
      );
    });
  }, [additions, search]);

  const additionTotalAdded = (row) =>
    (row?.items || []).reduce(
      (sum, it) => sum + (Number(it?.quantityAdded) || 0),
      0,
    );

  const headers = [
    {
      name: "Ekleyen",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a?.adderUserId?.name || "").localeCompare(
          String(b?.adderUserId?.name || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      name: "Kayıt Tarihi",
      dataType: "date",
      id: 1,
      customSorter: (a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
    {
      name: "Kalemler",
      dataType: "number",
      id: 2,
      customSorter: (a, b) => (a?.items || []).length - (b?.items || []).length,
    },
    {
      name: "Toplam Eklenen Ürün",
      dataType: "number",
      id: 3,
      customSorter: (a, b) => additionTotalAdded(a) - additionTotalAdded(b),
    },
    { name: "İşlemler", id: 4 },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("inventory-additions", headers);

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: "Envanter", url: "/inventory" },
    { label: "Envanter Girişleri" },
  ];

  return (
    <AppPage
      title="Envanter Girişleri"
      description="Yapılan envanter girişlerinin tamamını görüntüleyin"
      breadcrumbs={breadcrumbs}
      actions={
        canWrite ? (
          <Button
            appearance="primary-animated"
            onClick={() => navigate("/inventory/add")}
            icon={<FiPlus />}
          >
            Giriş Ekle
          </Button>
        ) : undefined
      }
    >
      <CardContainer>
        {loading ? (
          <div className="loading-container">
            <FiPackage className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <Table
            columns={headers}
            visibleColumns={visibleColumns}
            tableId="inventory-additions"
            loading={loading}
            viewOptions={
              <ListViewOptionsPanel
                columns={headers}
                visibleColumns={visibleColumns}
                onToggleColumn={toggleColumnVisibility}
                onResetColumns={resetToDefault}
                showApply={false}
                showReset={false}
              />
            }
          >
            {filtered.map((a) => {
              const total = additionTotalAdded(a);
              const itemCount = (a?.items || []).length;

              return (
                <Tr key={a._id} id={a._id} data={a}>
                  {visibleColumns.includes(0) && (
                    <Td>{a?.adderUserId?.name || "-"}</Td>
                  )}
                  {visibleColumns.includes(1) && (
                    <Td>{formatDateTime(a.createdAt)}</Td>
                  )}
                  {visibleColumns.includes(2) && (
                    <Td>
                      <Button
                        appearance="primary-subtle"
                        onClick={() =>
                          setDetailsModal({ isOpen: true, addition: a })
                        }
                      >
                        Detay ({itemCount})
                      </Button>
                    </Td>
                  )}
                  {visibleColumns.includes(3) && <Td>{total} adet</Td>}
                  {visibleColumns.includes(4) && (
                    <Td>
                      {canWrite && (
                        <IconButton
                          icon={<FiTrash2 style={{ fontSize: "18px" }} />}
                          appearance="subtle"
                          shape="circle"
                          onClick={() => handleDeleteAddition(a)}
                          title="Sil"
                        />
                      )}
                    </Td>
                  )}
                </Tr>
              );
            })}
          </Table>
        )}
      </CardContainer>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={detailsModal.isOpen}
        onClose={() => setDetailsModal({ isOpen: false, addition: null })}
        width="large"
      >
        <ModalHeader>Envanter Girişi Detayı</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <div className="small text-muted">
              Kayıt: {formatDateTime(detailsModal.addition?.createdAt)}
            </div>
            <div className="small text-muted">
              Ekleyen: {detailsModal.addition?.adderUserId?.name || "-"}
            </div>
            {!!detailsModal.addition?.notes && (
              <div className="small text-muted">
                Not: {detailsModal.addition?.notes}
              </div>
            )}
          </div>

          {(detailsModal.addition?.items || []).map((it, idx) => {
            const inv = it?.inventoryItemId;
            const name = inv?.partId?.name || inv?.name || "-";
            const codeVal = inv?.partId?.code || inv?.code || "";
            const code = codeVal ? `(${codeVal})` : "";
            const qty = Number(it?.quantityAdded) || 0;
            const pDate = it?.purchasedDate
              ? formatDate(it.purchasedDate)
              : "-";
            const note = it?.notes || "";
            return (
              <div
                key={idx}
                className="d-flex align-items-center justify-content-between border rounded p-2 mb-2"
              >
                <div>
                  <div>
                    <strong>
                      {name} {code}
                    </strong>
                  </div>
                  <div className="small text-muted">Satın alım: {pDate}</div>
                  {!!note && (
                    <div className="small text-muted">Not: {note}</div>
                  )}
                </div>
                <div className="text-end">
                  <div className="small text-muted">Miktar</div>
                  <div className="weight-600">+{qty}</div>
                </div>
              </div>
            );
          })}
          {(detailsModal.addition?.items || []).length === 0 && (
            <div className="text-muted">Kalem bulunamadı</div>
          )}
        </ModalBody>
        <ModalFooter>
          {canWrite && detailsModal.addition && (
            <Button
              appearance="danger"
              icon={<FiTrash2 />}
              onClick={() => handleDeleteAddition(detailsModal.addition)}
            >
              Sil
            </Button>
          )}
          <Button
            appearance="danger-subtle"
            onClick={() => setDetailsModal({ isOpen: false, addition: null })}
          >
            Kapat
          </Button>
        </ModalFooter>
      </Modal>
    </AppPage>
  );
};

export default withAuth(InventoryAdditions, ["admin", "employee", "user"]);
